import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight, Phone, MapPin, Clock, Siren, ChevronRight, CheckCircle2,
  Building2, Wrench,
} from "lucide-react";
import { SITE, SERVICES } from "../lib/site";
import { CITY_CONTENT } from "../lib/cityContent";
import QuoteForm from "../components/QuoteForm";

const BASE_URL = "https://alliancecraneservice.com";

export default function CityDetail() {
  const { slug } = useParams();
  const city = useMemo(() => CITY_CONTENT[slug], [slug]);

  if (!city) {
    return <Navigate to="/service-area" replace />;
  }

  const canonical = `${BASE_URL}/service-area/${slug}`;

  const relatedCities = (city.nearbyAreas || [])
    .map((s) => CITY_CONTENT[s])
    .filter(Boolean);

  const localBusinessLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${BASE_URL}/#business`,
    name: SITE.name,
    image: SITE.logo,
    url: canonical,
    telephone: SITE.phoneE164,
    email: SITE.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: "DeLand",
      addressLocality: "DeLand",
      addressRegion: "FL",
      postalCode: "32720",
      addressCountry: "US",
    },
    areaServed: {
      "@type": "City",
      name: `${city.name}, FL`,
      containedInPlace: { "@type": "State", name: "Florida" },
    },
    geo: { "@type": "GeoCoordinates", latitude: city.lat, longitude: city.lng },
    priceRange: "$$",
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Service Area", item: `${BASE_URL}/service-area` },
      { "@type": "ListItem", position: 3, name: city.name, item: canonical },
    ],
  };

  const faqLd = city.faqs && city.faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: city.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  } : null;

  return (
    <>
      <Helmet>
        <title>{`${city.metaTitle} | ${SITE.shortName}`}</title>
        <meta name="description" content={city.metaDescription} />
        <meta name="keywords" content={city.keywords} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={city.metaTitle} />
        <meta property="og:description" content={city.metaDescription} />
        <meta property="og:image" content={city.heroImage} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta name="geo.region" content="US-FL" />
        <meta name="geo.placename" content={`${city.name}, Florida`} />
        <meta name="geo.position" content={`${city.lat};${city.lng}`} />
        <meta name="ICBM" content={`${city.lat}, ${city.lng}`} />
        <script type="application/ld+json">{JSON.stringify(localBusinessLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
        {faqLd && <script type="application/ld+json">{JSON.stringify(faqLd)}</script>}
      </Helmet>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[var(--ac-border)]">
        <div className="absolute inset-0">
          <img
            src={city.heroImage}
            alt={`Mobile crane services in ${city.name}, Florida`}
            className="w-full h-full object-cover opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 pt-12 md:pt-24 pb-16 md:pb-28">
          <nav
            data-testid="breadcrumbs"
            aria-label="Breadcrumb"
            className="flex items-center gap-1.5 md:gap-2 text-[11px] md:text-xs uppercase tracking-[0.14em] md:tracking-[0.18em] font-bold text-zinc-400 mb-6 md:mb-8 flex-wrap"
          >
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/service-area" className="hover:text-white">Service Area</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[var(--ac-red)]">{city.name}</span>
          </nav>

          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 border border-[var(--ac-border)] flex items-center justify-center bg-black/70">
              <MapPin className="w-5 h-5 text-[var(--ac-red)]" />
            </div>
            <span className="overline">Service Area · {city.county}</span>
          </div>

          <h1
            data-testid="city-h1"
            className="font-display text-4xl sm:text-5xl lg:text-6xl max-w-4xl leading-[0.95]"
          >
            Crane Rental in <span className="text-[var(--ac-red)]">{city.name}, Florida</span>
          </h1>

          <p className="mt-4 md:mt-5 text-base md:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            {city.intro}
          </p>

          {/* Quick stats */}
          <div className="mt-8 md:mt-10 grid grid-cols-2 md:grid-cols-4 border-t border-white/10">
            <div className="py-4 border-r border-white/10 md:border-r">
              <div className="overline">Distance</div>
              <div className="font-display text-xl md:text-2xl mt-1">{city.distance}</div>
            </div>
            <div className="py-4 md:border-r border-white/10">
              <div className="overline">Drive Time</div>
              <div className="font-display text-xl md:text-2xl mt-1">{city.driveTime}</div>
            </div>
            <div className="py-4 border-r border-white/10">
              <div className="overline">County</div>
              <div className="font-display text-xl md:text-2xl mt-1">{city.county.replace(" County", "")}</div>
            </div>
            <div className="py-4">
              <div className="overline">Emergency</div>
              <div className="font-display text-xl md:text-2xl mt-1 text-[var(--ac-red)]">24 / 7</div>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Link to="/contact" className="btn-primary justify-center" data-testid="city-cta-quote">
              Request a Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`tel:${SITE.phoneE164}`} className="btn-ghost justify-center" data-testid="city-cta-call">
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
          </div>
        </div>
      </section>

      {/* COVERAGE + LOCAL NOTES */}
      <section className="py-14 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-7">
            <div className="overline mb-3">Coverage</div>
            <h2 className="font-display text-3xl md:text-4xl mb-5 leading-tight">
              Serving {city.name} contractors and property owners.
            </h2>
            <p className="text-zinc-300 leading-relaxed mb-5">{city.coverage}</p>
            {city.localNotes.map((n, i) => (
              <p key={i} className="text-zinc-400 leading-relaxed mb-4">{n}</p>
            ))}

            {city.commonProjects && city.commonProjects.length > 0 && (
              <div className="mt-10">
                <div className="overline mb-4">Common Projects in {city.name}</div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {city.commonProjects.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-zinc-200">
                      <CheckCircle2 className="w-5 h-5 text-[var(--ac-red)] mt-0.5 shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {city.neighborhoods && city.neighborhoods.length > 0 && (
              <div className="mt-10">
                <div className="overline mb-4">Neighborhoods & Corridors We Serve</div>
                <div className="flex flex-wrap gap-2">
                  {city.neighborhoods.map((n) => (
                    <span key={n} className="text-xs md:text-sm px-3 py-1.5 border border-[var(--ac-border)] bg-[var(--ac-bg-2)] text-zinc-300 font-mono-data tracking-wider">
                      {n}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="md:col-span-5">
            <div className="surface p-6 md:p-7">
              <div className="overline mb-4">Direct Line</div>
              <a
                href={`tel:${SITE.phoneE164}`}
                className="block group"
                data-testid="city-side-phone"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 border border-[var(--ac-border)] flex items-center justify-center bg-black">
                    <Phone className="w-4 h-4 text-[var(--ac-red)]" />
                  </div>
                  <div>
                    <div className="overline !text-[10px]">Phone</div>
                    <div className="font-display text-2xl group-hover:text-[var(--ac-red)] transition-colors">
                      {SITE.phone}
                    </div>
                  </div>
                </div>
              </a>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex items-center gap-2 text-zinc-300">
                  <Clock className="w-4 h-4 text-[var(--ac-red)]" /> {SITE.hours}
                </div>
                <div className="flex items-center gap-2 text-[var(--ac-red)] font-semibold">
                  <Siren className="w-4 h-4" /> {SITE.emergencyHours}
                </div>
              </div>
              <Link
                to="/contact"
                className="btn-primary w-full justify-center mt-6"
                data-testid="city-side-quote"
              >
                Request Quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="surface p-6 md:p-7 mt-5">
              <div className="overline mb-4">Services Available in {city.name}</div>
              <ul className="space-y-3">
                {SERVICES.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/services/${s.slug}/${slug}`}
                      data-testid={`city-service-${s.slug}`}
                      className="flex items-start gap-3 text-zinc-300 hover:text-[var(--ac-red)] transition-colors group"
                    >
                      <Wrench className="w-4 h-4 mt-1 text-[var(--ac-red)] shrink-0" />
                      <span className="font-display tracking-wide text-lg">{s.title} in {city.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      {city.faqs && city.faqs.length > 0 && (
        <section className="py-14 md:py-24 bg-[var(--ac-bg-2)] border-y border-[var(--ac-border)]">
          <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="overline mb-4">Questions</div>
              <h2 className="font-display text-4xl md:text-5xl leading-[0.95]">
                {city.name}<br /> FAQs.
              </h2>
            </div>
            <div className="md:col-span-8 border-t border-[var(--ac-border)]">
              {city.faqs.map((f, i) => (
                <details
                  key={i}
                  data-testid={`city-faq-${i}`}
                  className="group border-b border-[var(--ac-border)] py-5"
                >
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                    <span className="font-display text-lg md:text-xl leading-snug">{f.q}</span>
                    <ChevronRight className="w-5 h-5 shrink-0 text-[var(--ac-red)] mt-1 transition-transform group-open:rotate-90" />
                  </summary>
                  <p className="mt-3 text-zinc-400 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RELATED CITIES */}
      {relatedCities.length > 0 && (
        <section className="py-14 md:py-24">
          <div className="max-w-[1400px] mx-auto px-5 md:px-8">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <div className="overline mb-3">Nearby</div>
                <h2 className="font-display text-3xl md:text-4xl">Also in the area.</h2>
              </div>
              <Link to="/service-area" className="btn-ghost" data-testid="city-view-all-areas">
                All Service Areas <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-[var(--ac-border)]">
              {relatedCities.map((c) => (
                <Link
                  key={c.slug}
                  to={`/service-area/${c.slug}`}
                  data-testid={`related-city-${c.slug}`}
                  className="hover-card-border border-b border-r border-[var(--ac-border)] p-6 group bg-[var(--ac-bg)]"
                >
                  <MapPin className="w-5 h-5 text-[var(--ac-red)] mb-3" />
                  <div className="font-display text-2xl">{c.name}</div>
                  <div className="overline mt-1 !text-[10px]">{c.county}</div>
                  <div className="mt-3 text-xs font-mono-data text-zinc-500">{c.distance}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* QUOTE CTA */}
      <section className="py-14 md:py-24 bg-black border-t border-[var(--ac-border)]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10">
          <div className="lg:col-span-5">
            <div className="overline mb-3 text-[var(--ac-red)]">Get Started</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95]">
              Book a lift in<br /> {city.name}.
            </h2>
            <p className="mt-5 text-zinc-400 leading-relaxed max-w-md">
              Tell us about the job and we&apos;ll get back fast with availability and pricing.
              Emergency? Call our 24/7 line directly.
            </p>
            <a
              href={`tel:${SITE.phoneE164}`}
              className="btn-primary mt-6"
              data-testid="city-bottom-call"
            >
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
          </div>
          <div className="lg:col-span-7">
            <QuoteForm />
          </div>
        </div>
      </section>
    </>
  );
}
